import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import "./style.css";
import axios from "axios";

export default function CourseFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [courses, setCourses] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const filters = [
    { id: "all", name: "Todos" },
    { id: "free", name: "Gratuitos" },
    { id: "paid", name: "Pagos" },
    { id: "popular", name: "Populares" },
    { id: "new", name: "Novos" },
    { id: "enrolled", name: "Meus Cursos" },
  ];

  const fetchCourses = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        filter,
        page: pageNum,
        limit: 12,
        ...(searchTerm && { search: searchTerm })
      });

      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/courses?${params}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      if (pageNum === 1) setCourses(data.posts || []);
      else setCourses((prev) => [...prev, ...(data.posts || [])]);
      setPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setLoading(false);
    }
  }, [filter, searchTerm, user.token]);

  const fetchTrending = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/trending/courses?limit=6`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setTrending(data.trending || []);
    } catch (error) {
      console.error("Error fetching trending:", error);
    }
  }, [user.token]);

  const fetchLeaderboard = useCallback(
    async (courseId) => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/courses/${courseId}/leaderboard?limit=10`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        setLeaderboard(data.leaderboard || []);
        setShowLeaderboard(true);
      } catch (error) {
        alert(error.response?.data?.error || "Erro ao carregar leaderboard");
      }
    },
    [user.token]
  );

  useEffect(() => {
    setPage(1);
    fetchCourses(1);
  }, [filter, searchTerm, fetchCourses]);

  useEffect(() => {
    fetchTrending();
  }, [fetchTrending]);

  const handleEnrollFree = async (courseId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/courses/${courseId}/enroll`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Inscrito com sucesso!");
      setSelectedCourse(null);
      setShowDetailsModal(false);
      fetchCourses(1);
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao inscrever");
    }
  };

  const handlePayWithWallet = async () => {
    try {
      setPaymentLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/courses/${selectedCourse._id}/pay`,
        { paymentMethod: "wallet" },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Compra realizada com sucesso!");
      setShowPaymentModal(false);
      setSelectedCourse(null);
      setShowDetailsModal(false);
      fetchCourses(1);
    } catch (error) {
      alert(error.response?.data?.error || "Erro ao processar pagamento");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (page < pagination.pages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchCourses(nextPage);
    }
  };

  const openCourseDetails = (course) => {
    setSelectedCourse(course);
    setShowDetailsModal(true);
  };

  if (loading && courses.length === 0) {
    return <div className="course_feed_loading"><div className="course_loader"></div><span>Carregando cursos...</span></div>;
  }

  return (
    <div className="course_feed">
      {/* Header com busca */}
      <div className="course_feed_header">
        <h1>📚 Cursos</h1>
        <input
          type="text"
          placeholder="Buscar cursos..."
          className="course_search_input"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Trending Section */}
      {trending.length > 0 && filter === 'all' && !searchTerm && (
        <div className="course_trending_section">
          <h3>🔥 Em Tendência</h3>
          <div className="course_trending_grid">
            {trending.map((course) => (
              <div key={course._id} className="course_trending_card" onClick={() => openCourseDetails(course)}>
                <div className="course_trending_image" style={{ backgroundImage: `url(${course.coverImage})` }}>
                  {course.isFree ? <span className="badge_free">GRÁTIS</span> : <span className="badge_paid">PAGO</span>}
                </div>
                <div className="course_trending_info">
                  <h4>{course.title}</h4>
                  <p className="trending_students">👥 {course.stats.studentsCount} alunos</p>
                  <div className="trending_rating">⭐ {course.stats.ratingAverage}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      {filters.length > 0 && (
        <div className="course_filters">
          {filters.map((f) => (
            <button
              key={f.id}
              className={`course_filter_btn ${filter === f.id ? "active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="course_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
          </svg>
          <p>Nenhum curso encontrado</p>
        </div>
      ) : (
        <div className="course_grid">
          {courses.map((course) => (
            <div key={course._id} className="course_card">
              <div className="course_cover" onClick={() => openCourseDetails(course)} style={{ backgroundImage: `url(${course.coverImage})` }}>
                {course.isFree ? <span className="badge_free">GRÁTIS</span> : <span className="badge_paid">PAGO</span>}
                {course.userState?.enrolled && <span className="badge_enrolled">Inscrito</span>}
                {course.userState?.progress > 0 && (
                  <div className="course_progress_bar">
                    <div className="course_progress_fill" style={{ width: `${course.userState.progress}%` }}></div>
                  </div>
                )}
              </div>
              <div className="course_info">
                <h3 onClick={() => openCourseDetails(course)}>{course.title}</h3>
                <p className="course_subtitle">{course.subtitle}</p>
                <div className="course_meta">
                  <span className="course_rating">⭐ {course.stats.ratingAverage}</span>
                  <span className="course_students">👥 {course.stats.studentsCount}</span>
                  <span className="course_duration">⏱ {course.duration}h</span>
                  {!course.isFree && (
                    <span className="course_price">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                      </svg>
                      R$ {course.price.toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="course_actions_row">
                  <button
                    className="course_btn course_btn_secondary"
                    onClick={() => fetchLeaderboard(course._id)}
                  >
                    🏆 Leaderboard
                  </button>
                </div>

                {course.userState?.enrolled ? (
                  <button
                    className="course_btn course_btn_continue"
                    onClick={() => setSelectedCourse(course)}
                  >
                    Continuar
                  </button>
                ) : course.isFree ? (
                  <button
                    className="course_btn course_btn_enroll"
                    onClick={() => handleEnrollFree(course._id)}
                  >
                    Inscrever-se
                  </button>
                ) : (
                  <button
                    className="course_btn course_btn_buy"
                    onClick={() => {
                      setSelectedCourse(course);
                      setShowPaymentModal(true);
                    }}
                  >
                    💳 Comprar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {courses.length > 0 && pagination.pages > page && (
        <div className="course_load_more">
          <button
            className="course_btn course_btn_load_more"
            onClick={() => { setPage(page + 1); fetchCourses(page + 1); }}
            disabled={loading}
          >
            {loading ? "Carregando..." : "Carregar mais"}
          </button>
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedCourse && !showPaymentModal && (
        <div className="course_modal_overlay" onClick={() => setSelectedCourse(null)}>
          <div className="course_modal_content" onClick={(e) => e.stopPropagation()}>
            <button className="course_modal_close" onClick={() => setSelectedCourse(null)}>✕</button>
            <div className="course_modal_header">
              <img src={selectedCourse.coverImage} alt={selectedCourse.title} className="course_modal_image" />
            </div>
            <div className="course_modal_body">
              <h2>{selectedCourse.title}</h2>
              <p className="course_modal_subtitle">{selectedCourse.subtitle}</p>
              <p className="course_modal_description">{selectedCourse.description}</p>

              <div className="course_modules">
                <h3>📖 Conteúdo do Curso</h3>
                {selectedCourse.modules?.map((module, idx) => (
                  <div key={idx} className="course_module">
                    <h4>{module.title}</h4>
                    <ul>
                      {module.lessons?.map((lesson) => (
                        <li key={lesson._id}>
                          {lesson.locked && !selectedCourse.userState?.paid ? "🔒" : "▶"}
                          {" "}{lesson.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="course_reviews_preview">
                <h3>⭐ Avaliações</h3>
                <p>{selectedCourse.stats.ratingAverage} / 5 ({selectedCourse.stats.ratingsCount} avaliações)</p>
              </div>
            </div>

            <div className="course_modal_footer">
              {selectedCourse.userState?.enrolled ? (
                <button className="course_btn course_btn_continue">Continuar Curso</button>
              ) : selectedCourse.isFree ? (
                <button className="course_btn course_btn_enroll" onClick={() => handleEnrollFree(selectedCourse._id)}>
                  Inscrever-se
                </button>
              ) : (
                <button
                  className="course_btn course_btn_buy"
                  onClick={() => setShowPaymentModal(true)}
                >
                  💳 Comprar com Wallet — R$ {selectedCourse.price.toFixed(2)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selectedCourse && (
        <div className="course_modal_overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="course_payment_modal" onClick={(e) => e.stopPropagation()}>
            <button className="course_modal_close" onClick={() => setShowPaymentModal(false)}>✕</button>

            <h2>💳 Confirmar Compra</h2>
            <div className="course_payment_summary">
              <img src={selectedCourse.coverImage} alt={selectedCourse.title} className="course_payment_image" />
              <h3>{selectedCourse.title}</h3>
              <div className="course_payment_price">
                <span>Preço:</span>
                <strong>R$ {selectedCourse.price.toFixed(2)}</strong>
              </div>
              <div className="course_payment_method">
                <span>Método de Pagamento:</span>
                <strong>💰 Wallet Socializa</strong>
              </div>
            </div>

            <div className="course_payment_warning">
              ⚠️ O valor será debitado de sua Wallet. Você será solicitado a confirmar com seu PIN.
            </div>

            <div className="course_payment_actions">
              <button
                className="course_btn course_btn_secondary"
                onClick={() => setShowPaymentModal(false)}
                disabled={paymentLoading}
              >
                Cancelar
              </button>
              <button
                className="course_btn course_btn_primary"
                onClick={handlePayWithWallet}
                disabled={paymentLoading}
              >
                {paymentLoading ? "Processando..." : "Confirmar Pagamento"}
              </button>
            </div>
          </div>
        </div>
      )}



      {page < pagination.pages && (
        <div className="course_load_more">
          <button onClick={handleLoadMore} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais cursos"}
          </button>
        </div>
      )}

      {/* Modal Detalhes */}
      {showDetailsModal && selectedCourse && (
        <div className="course_modal_overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="course_modal_content" onClick={(e) => e.stopPropagation()}>
            <button className="course_modal_close" onClick={() => setShowDetailsModal(false)}>✕</button>
            <div className="course_modal_header">
              <img src={selectedCourse.coverImage} alt={selectedCourse.title} className="course_modal_image" />
            </div>
            <div className="course_modal_body">
              <h2>{selectedCourse.title}</h2>
              <p className="course_modal_subtitle">{selectedCourse.subtitle}</p>
              <p className="course_modal_description">{selectedCourse.description}</p>

              <div className="course_modules">
                <h3>📖 Conteúdo do Curso</h3>
                {selectedCourse.modules?.map((module, idx) => (
                  <div key={idx} className="course_module">
                    <h4>{module.title}</h4>
                    <ul>
                      {module.lessons?.map((lesson) => (
                        <li key={lesson._id}>
                          {lesson.locked && !selectedCourse.userState?.paid ? "🔒" : "▶"} {lesson.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="course_reviews_preview">
                <h3>⭐ Avaliações</h3>
                <p>{selectedCourse.stats.ratingAverage} / 5 ({selectedCourse.stats.ratingsCount} avaliações)</p>
              </div>
            </div>

            <div className="course_modal_footer">
              {selectedCourse.userState?.enrolled ? (
                <button className="course_btn course_btn_continue">Continuar Curso</button>
              ) : selectedCourse.isFree ? (
                <button className="course_btn course_btn_enroll" onClick={() => handleEnrollFree(selectedCourse._id)}>
                  Inscrever-se
                </button>
              ) : (
                <button
                  className="course_btn course_btn_buy"
                  onClick={() => { setShowDetailsModal(false); setShowPaymentModal(true); }}
                >
                  💳 Comprar com Wallet — R$ {selectedCourse.price.toFixed(2)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Leaderboard */}
      {showLeaderboard && (
        <div className="course_modal_overlay" onClick={() => setShowLeaderboard(false)}>
          <div className="course_modal_content" onClick={(e) => e.stopPropagation()}>
            <button className="course_modal_close" onClick={() => setShowLeaderboard(false)}>✕</button>
            <h2>🏆 Leaderboard</h2>
            <div className="course_leaderboard_list">
              {leaderboard.map((item, idx) => (
                <div key={item._id} className="leaderboard_item">
                  <span className="leaderboard_rank">#{idx + 1}</span>
                  <img src={item.userId?.picture} alt="" className="leaderboard_avatar" />
                  <div className="leaderboard_info">
                    <p className="leaderboard_name">{item.userId?.first_name} {item.userId?.last_name}</p>
                    <p className="leaderboard_progress">{item.percent}% completo</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
